import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import type {BaseFilterOption} from '@/types/models/creator-crm/filter';
import type {CampaignCreator} from '@/views/campaigns/content-submissions/types/campaignCreator';
import {INITIAL_FILTERS, type ContentSubmissionsFilters} from './filterConfig';

export type ContentSubmissionsState = {
  isFilterDrawerOpen: boolean;
  filters: ContentSubmissionsFilters;
  appliedFilters: ContentSubmissionsFilters;
};

const initialState: ContentSubmissionsState = {
  isFilterDrawerOpen: false,
  filters: {...INITIAL_FILTERS},
  appliedFilters: {...INITIAL_FILTERS},
};

const useContentSubmissionsStore = create<ContentSubmissionsState>()(
  immer(() => initialState)
);

function getActiveFilterCount(filters: ContentSubmissionsFilters): number {
  let count = 0;
  if (filters.talent) count += 1;
  count += filters.contentType.length;
  count += filters.status.length;
  return count;
}

const openFilterDrawer = () =>
  useContentSubmissionsStore.setState((draft) => {
    draft.isFilterDrawerOpen = true;
  });

const closeFilterDrawer = () =>
  useContentSubmissionsStore.setState((draft) => {
    draft.isFilterDrawerOpen = false;
    draft.filters = draft.appliedFilters;
  });

const applyFilters = () =>
  useContentSubmissionsStore.setState((draft) => {
    draft.appliedFilters = draft.filters;
    draft.isFilterDrawerOpen = false;
  });

const clearFilters = () =>
  useContentSubmissionsStore.setState((draft) => {
    draft.filters = {...INITIAL_FILTERS};
  });

const clearAndApplyFilters = () =>
  useContentSubmissionsStore.setState((draft) => {
    draft.filters = {...INITIAL_FILTERS};
    draft.appliedFilters = {...INITIAL_FILTERS};
  });

const restoreFilters = (restored: ContentSubmissionsFilters) =>
  useContentSubmissionsStore.setState((draft) => {
    draft.filters = restored;
    draft.appliedFilters = restored;
  });

const setTalent = (option: CampaignCreator | null) =>
  useContentSubmissionsStore.setState((draft) => {
    draft.filters.talent = option;
  });

const setAndApplyTalent = (option: CampaignCreator | null) =>
  useContentSubmissionsStore.setState((draft) => {
    draft.filters.talent = option;
    draft.appliedFilters.talent = option;
  });

type MultiSelectFilterId = 'contentType' | 'status';

const toggleFilterOption = (
  filterId: MultiSelectFilterId,
  option: BaseFilterOption
) =>
  useContentSubmissionsStore.setState((draft) => {
    const list = draft.filters[filterId];
    const index = list.findIndex((item) => item.id === option.id);
    if (index >= 0) {
      list.splice(index, 1);
    } else {
      list.push(option);
    }
  });

export const contentSubmissionsActions = {
  openFilterDrawer,
  closeFilterDrawer,
  applyFilters,
  clearFilters,
  clearAndApplyFilters,
  restoreFilters,
  setTalent,
  setAndApplyTalent,
  toggleFilterOption,
  getActiveFilterCount,
};

export default useContentSubmissionsStore;
